import * as React from 'react';
import { Check, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type SyncStatusType = 'synced' | 'pending' | 'failed' | null;

export interface SyncStatusBadgeProps {
  status: SyncStatusType;
  error?: string;
  onRetry?: () => void;
  className?: string;
  showLabel?: boolean;
}

/**
 * Visual indicator for Encompass field sync status.
 * Shows sync state with tooltip and optional retry button.
 */
export function SyncStatusBadge({
  status,
  error,
  onRetry,
  className,
  showLabel = false
}: SyncStatusBadgeProps) {
  if (!status) {
return null;
}

  const config = {
    synced: {
      icon: Check,
      label: 'Synced',
      tooltip: 'Synced to Encompass',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600'
    },
    pending: {
      icon: Clock,
      label: 'Pending',
      tooltip: error || 'Waiting to sync to Encompass',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-200',
      iconColor: 'text-yellow-600'
    },
    failed: {
      icon: AlertCircle,
      label: 'Failed',
      tooltip: error || 'Failed to sync to Encompass',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600'
    }
  };

  const { icon: Icon, label, tooltip, bgColor, textColor, borderColor, iconColor } = config[status];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
              bgColor,
              textColor,
              borderColor,
              className
            )}
          >
            <Icon className={cn('h-3 w-3', iconColor)} />
            {showLabel && <span>{label}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="flex flex-col gap-1">
            <span>{tooltip}</span>
            {status === 'failed' && onRetry && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className="mt-1 h-6 gap-1 text-xs"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Inline sync status indicator (icon only, minimal space)
 */
export function SyncStatusIcon({
  status,
  error,
  className
}: {
  status: SyncStatusType;
  error?: string;
  className?: string;
}) {
  if (!status) {
return null;
}

  const config = {
    synced: {
      icon: Check,
      color: 'text-green-600',
      tooltip: 'Synced to Encompass'
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      tooltip: error || 'Pending sync to Encompass'
    },
    failed: {
      icon: AlertCircle,
      color: 'text-red-600',
      tooltip: error || 'Failed to sync'
    }
  };

  const { icon: Icon, color, tooltip } = config[status];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Icon className={cn('h-4 w-4', color, className)} />
        </TooltipTrigger>
        <TooltipContent side="top">
          <span>{tooltip}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
